import pLimit from 'p-limit';
import { match, matcher, variant, payload, fields, isType } from 'variant';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { HttpError } from '@wix/cli-http-client';
import type { SiteAuth, UserInfo } from '../schemas.js';
import { client, RenewTokenData } from '../client.js';
import { isValidToken, readSiteAuthData, saveAuthData } from '../auth-data.js';
import type { AuthStrategyState, AuthStrategy } from './auth-strategy-types.js';
import { apiKeyAuthStrategy } from './api-key-auth-strategy.js';
import { accountAuthStrategy } from './account-auth-strategy.js';

async function renewSiteAuth({
  accountRefreshToken,
  siteId,
}: {
  accountRefreshToken: string;
  siteId: string;
}) {
  const renewTokenResult = await client.renewToken({
    refreshToken: accountRefreshToken,
    siteId,
  });

  if (isType(renewTokenResult, RenewTokenData.TokenRenewed)) {
    await saveAuthData(renewTokenResult.payload, siteId);
  }

  return renewTokenResult;
}

export const AuthenticationResult = variant({
  Ok: payload<SiteAuth>(),
  RefreshTokenRevoked: fields<{ cause: HttpError }>(),
});

export async function ensureAuthenticatedToSite({
  siteId,
  accountRefreshToken,
}: {
  siteId: string;
  accountRefreshToken: string;
}) {
  const siteAuthStatus = await readSiteAuthData(siteId);

  return matcher(siteAuthStatus)
    .with({ Authenticated: (data) => AuthenticationResult.Ok(data) })
    .when(['Unauthenticated', 'Expired'], async () => {
      const renewTokenResult = await renewSiteAuth({
        accountRefreshToken,
        siteId,
      });

      return match(renewTokenResult, {
        TokenRenewed: ({ payload }) => AuthenticationResult.Ok(payload),
        RefreshTokenRevoked: ({ cause }) => {
          return AuthenticationResult.RefreshTokenRevoked({ cause });
        },
      });
    })
    .complete();
}

function createSiteAuthState({
  siteAuth: initialTokenData,
  accountRefreshToken,
  siteId,
  userInfo,
}: {
  siteId: string;
  accountRefreshToken: string;
  userInfo: UserInfo;
  siteAuth: SiteAuth;
}): AuthStrategyState {
  const limiter = pLimit(1);
  let tokenData = { ...initialTokenData };

  return {
    getAccessToken: () => {
      return limiter(async () => {
        if (isValidToken(tokenData)) {
          return tokenData.accessToken;
        }

        const renewTokenResult = await renewSiteAuth({
          accountRefreshToken,
          siteId,
        });

        return match(renewTokenResult, {
          TokenRenewed: ({ payload: newTokenData }) => {
            tokenData = newTokenData;
            return tokenData.accessToken;
          },
          RefreshTokenRevoked: ({ cause }) => {
            throw new CliError({
              code: CliErrorCode.FailedToRenewAuthTokenRefreshTokenRevoked(),
              cause,
            });
          },
        });
      });
    },
    getRefreshToken: () => tokenData.refreshToken,
    userInfo,
  };
}

export const createSiteAuthStrategy = (siteId: string): AuthStrategy => ({
  async isLoggedIn() {
    const apiKeyAuth = await apiKeyAuthStrategy.isLoggedIn({ siteId });

    if (apiKeyAuth) {
      return apiKeyAuth;
    }

    const accountAuth = await accountAuthStrategy.isLoggedIn();

    if (accountAuth) {
      const accountRefreshToken = accountAuth.getRefreshToken();
      const siteAuthResult = await ensureAuthenticatedToSite({
        siteId,
        accountRefreshToken,
      });

      if (isType(siteAuthResult, AuthenticationResult.Ok)) {
        return createSiteAuthState({
          siteId,
          siteAuth: siteAuthResult.payload,
          accountRefreshToken,
          userInfo: accountAuth.userInfo,
        });
      }
    }

    return null;
  },
  async login({ deviceCode, signal }) {
    const accountAuth = await accountAuthStrategy.login({ deviceCode, signal });
    const accountRefreshToken = accountAuth.getRefreshToken();
    const siteAuthResult = await ensureAuthenticatedToSite({
      siteId,
      accountRefreshToken,
    });

    return match(siteAuthResult, {
      Ok: ({ payload: siteAuth }) => {
        return createSiteAuthState({
          siteId,
          siteAuth,
          accountRefreshToken,
          userInfo: accountAuth.userInfo,
        });
      },
      RefreshTokenRevoked: ({ cause }) => {
        throw new CliError({
          code: CliErrorCode.FailedToRenewAuthTokenRefreshTokenRevoked(),
          cause,
        });
      },
    });
  },
});
